<template>
    <RegularModal ref="modal" :modal-card-class="'is-large'" class="auto-seed-modal">
        <template v-slot:title>
            {{ translate("admin_bracket_listing.generate_all_brackets") }}
        </template>
        <template v-slot:body>
            <template v-if="initDone && !reloadingInProgress">
                <template v-if="!store.disciplinesNotConfigured && !store.noDisciplinesDetected">
                    <div class="columns summary-header">
                        <div class="column is-12 is-flex is-align-items-center is-flex-wrap-wrap">
                            <div class="field is-grouped is-flex-grow-1">
                                <AutoSeedingStatusSummary
                                    :text="translate('admin_bracket_listing.status_new')"
                                    :counter="counterCreate"
                                    :counter-class="'is-success'"
                                />

                                <AutoSeedingStatusSummary
                                    :text="translate('admin_bracket_listing.status_update')"
                                    :counter="counterUpdate"
                                    :counter-class="'is-warning'"
                                />

                                <AutoSeedingStatusSummary
                                    :text="translate('admin_bracket_listing.status_delete')"
                                    :counter="counterDelete"
                                    :counter-class="'is-danger'"
                                />
                            </div>

                            <div class="is-flex is-flex-direction-column is-align-items-flex-end">
                                <BracketSeedingSelector v-model="selectedSeedingType" />
                                <Checkbox2 v-model="includeManualBrackets" class="mt-2">
                                    {{ translate("admin_bracket_listing.include_manual_brackets") }}
                                </Checkbox2>
                            </div>
                        </div>
                    </div>
                </template>

                <BracketsDisciplineStatusNotifications :event-id="props.eventId" />

                <template v-if="!store.noDisciplinesDetected && !store.disciplinesNotConfigured">
                    <template v-if="autoSeedingPreview.length > 0">
                        <div class="columns category-row select-all-row">
                            <div class="column is-12">
                                <Checkbox2 v-model="selectAllModel">
                                    {{ translate(selectAllModel ? "common.deselect_all" : "common.select_all") }}
                                </Checkbox2>
                            </div>
                        </div>

                        <div v-for="(autoSeedingStatus, index) in autoSeedingPreview" class="columns category-row" :key="index">
                            <div class="column is-narrow">
                                <Checkbox2
                                    v-model="selectedParameterIds[autoSeedingStatus.parameterId]"
                                    :disabled="!!autoSeedingStatus.blockReason"
                                />
                            </div>

                            <div class="column" :class="getStatusClass(autoSeedingStatus.status)">
                                {{ autoSeedingStatus.categoryName }}
                                ({{ autoSeedingStatus.size }})
                            </div>

                            <div class="column">
                                {{ translateBracketType(autoSeedingStatus.targetBracketType) }}
                            </div>

                            <div class="column">
                                <span class="has-text-danger" v-if="autoSeedingStatus.blockReason">
                                    {{ translate(autoSeedingStatus.blockReason) }}
                                </span>
                                <template v-for="reason in autoSeedingStatus.outdatedReasons" :key="reason">
                                    <span class="has-text-warning">
                                        <span class="icon is-small mr-1">
                                            <i :class="getOutdatedReasonIcon(reason)" aria-hidden="true"></i>
                                        </span>
                                        <span>
                                            {{ translate(reason) }}
                                        </span>
                                    </span>
                                </template>
                            </div>
                        </div>
                    </template>
                    <div v-else class="columns">
                        <div class="column is-12">
                            <p class="is-size-5">
                                <span class="icon">
                                    <i class="fa fa-thumbs-up" />
                                </span>
                                <span>
                                    {{ translate("admin_bracket_listing.all_up_to_date") }}
                                </span>
                            </p>
                        </div>
                    </div>
                </template>
            </template>
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton
                    class="button"
                    :disabled="!canConfirmAutoSeeding"
                    :class="confirmButtonClass"
                    :is-loading="autoSeedingInProgress"
                    @click="autoSeedBrackets"
                >
                    {{ confirmButtonText }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { reactive, ref, computed, watch } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import AutoSeedingStatusSummary from "./AutoSeedingStatusSummary.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import BracketSeedingSelector from "../BracketSeedingSelector.vue";
    import BracketsDisciplineStatusNotifications from "./BracketsDisciplineStatusNotifications.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { BracketSeedingTypeEnum } from "../../Common/types";
    import { translate } from "../../Common/translations";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";
    import bracketsApi, {
        AutoSeedingPreview,
        AutoSeedingPreviewResponse,
        BracketTypeConstraintWithTranslationKey,
    } from "../../Api/brackets";
    import { useModal } from "../../Common/useModal";
    import { useBracketsListingStore } from "./bracketsListingApp";

    const props = defineProps<{
        eventId: number;
    }>();
    const { open: openModal, close: closeModal, modal } = useModal();
    const initDone = ref(false);
    const autoSeedingInProgress = ref(false);
    const autoSeedingPreview = ref<AutoSeedingPreview[]>([]);
    const bracketTypesList = ref<BracketTypeConstraintWithTranslationKey[]>([]);
    const selectedSeedingType = ref(BracketSeedingTypeEnum.ByRankingAndAcademy);
    const includeManualBrackets = ref(false);
    const selectedParameterIds = reactive<Record<number, boolean>>({});
    const store = useBracketsListingStore();

    const selectableParameterIds = computed(() =>
        autoSeedingPreview.value.filter(status => !status.blockReason).map(status => status.parameterId),
    );

    function selectedCount() {
        return selectableParameterIds.value.filter(parameterId => selectedParameterIds[parameterId]).length;
    }

    const isAllSelected = computed(
        () => selectableParameterIds.value.length > 0 && selectedCount() === selectableParameterIds.value.length,
    );

    const selectAllModel = computed({
        get: () => isAllSelected.value,
        set: (value: boolean) => {
            selectableParameterIds.value.forEach(parameterId => {
                selectedParameterIds[parameterId] = value;
            });
        },
    });

    function countSelectedByStatus(status: "new" | "update" | "delete") {
        return autoSeedingPreview.value.filter(s => s.status === status && selectedParameterIds[s.parameterId]).length;
    }

    const counterCreate = computed(() => countSelectedByStatus("new"));
    const counterUpdate = computed(() => countSelectedByStatus("update"));
    const counterDelete = computed(() => countSelectedByStatus("delete"));
    const counterBlocked = computed(() => autoSeedingPreview.value.filter(s => s.blockReason !== null).length);

    const outdatedReasonIcons: Record<string, string> = {
        "admin_bracket_listing.bracket_settings_changed": "fas fa-wrench",
        "admin_bracket_listing.bracket_type_changed": "fas fa-sitemap",
        "admin_bracket_listing.bracket_type_manually_changed": "fas fa-user-edit",
        "admin_bracket_listing.registrations_changed": "fas fa-users",
        "admin_bracket_listing.seeding_type_changed": "fas fa-random",
    };

    const confirmButtonText = computed(() => {
        if (counterBlocked.value === 0) {
            return translate("common.ok");
        }
        return `${translate("common.ok")} (${translate("admin_bracket_listing.problems_found")})`;
    });

    const confirmButtonClass = computed(() => {
        if (counterBlocked.value === 0) {
            return "is-primary";
        }
        return "is-danger";
    });

    const canConfirmAutoSeeding = computed(
        () =>
            selectableParameterIds.value.length > 0 &&
            selectedCount() > 0 &&
            !store.disciplinesNotConfigured &&
            !store.noDisciplinesDetected,
    );

    function resetPreviewCounters() {
        autoSeedingPreview.value = [];
        Object.keys(selectedParameterIds).forEach(parameterId => {
            delete selectedParameterIds[Number(parameterId)];
        });
    }

    const reloadingInProgress = ref(false);

    function fetchAutoSeedingPreview() {
        reloadingInProgress.value = true;
        return bracketsApi
            .getAutoSeedingPreview(props.eventId, selectedSeedingType.value, includeManualBrackets.value)
            .then((autoSeedingPreviewResponse: AutoSeedingPreviewResponse) => {
                autoSeedingPreview.value = autoSeedingPreviewResponse;

                Object.keys(selectedParameterIds).forEach(parameterId => {
                    delete selectedParameterIds[Number(parameterId)];
                });
                autoSeedingPreviewResponse.forEach(seedingStatus => {
                    if (!seedingStatus.blockReason) {
                        selectedParameterIds[seedingStatus.parameterId] = true;
                    }
                });
            })
            .finally(() => {
                reloadingInProgress.value = false;
            });
    }

    watch([selectedSeedingType, includeManualBrackets], () => {
        if (!initDone.value) {
            return;
        }
        resetPreviewCounters();
        fetchAutoSeedingPreview().catch(handleError);
    });

    function autoSeedBrackets() {
        autoSeedingInProgress.value = true;
        const parameterIds = selectableParameterIds.value.filter(parameterId => selectedParameterIds[parameterId]);
        return bracketsApi
            .autoSeedBrackets(props.eventId, selectedSeedingType.value, includeManualBrackets.value, parameterIds)
            .then(() => {
                successToast(translate("admin_bracket_listing.done"));
                close();
                return store.refreshBracketsListing();
            })
            .catch(handleError)
            .finally(() => {
                autoSeedingInProgress.value = false;
            });
    }

    function open() {
        autoSeedingInProgress.value = false;
        initDone.value = false;
        openModal();

        resetPreviewCounters();
        selectedSeedingType.value = BracketSeedingTypeEnum.ByRankingAndAcademy;
        includeManualBrackets.value = false;

        Promise.all([bracketsApi.getBracketTypes(props.eventId), fetchAutoSeedingPreview()])
            .then(([typesMap]) => {
                bracketTypesList.value = typesMap;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function close() {
        closeModal();
    }

    function translateBracketType(bracketType) {
        const foundBracketType = bracketTypesList.value.find(b => b.bracketType === bracketType);
        if (!foundBracketType) {
            return "";
        }
        return translate(foundBracketType.name);
    }

    function getStatusClass(status) {
        if (status === "new") {
            return "has-text-success";
        }
        if (status === "update") {
            return "has-text-warning";
        }
        if (status === "delete") {
            return "has-text-danger";
        }
        return "";
    }

    function getOutdatedReasonIcon(reason: string) {
        return outdatedReasonIcons[reason] ?? "fas fa-exclamation-triangle";
    }

    defineExpose({ open, close });
</script>

<style scoped></style>
